import React, { useState, useRef, useEffect, useCallback } from 'react';
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";

// Create a global intersection observer for better performance
let intersectionObserver;
const observedElements = new WeakMap();
const imageCache = new Map();

// Preload critical images
const preloadImage = (src) => {
  if (imageCache.has(src)) return imageCache.get(src);
  
  const promise = new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
  
  imageCache.set(src, promise);
  return promise;
};

// Initialize intersection observer
const getIntersectionObserver = () => {
  if (!intersectionObserver) {
    intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const callback = observedElements.get(entry.target);
          if (callback) {
            callback(entry.isIntersecting);
          }
        });
      },
      {
        rootMargin: '50px 0px', // Start loading 50px before element comes into view
        threshold: 0.1,
      }
    );
  }
  return intersectionObserver;
};

const ImageWithFallback = ({ 
  src, 
  fallback, 
  priority = false, 
  delay = 0,
  className = '',
  alt = '',
  onLoad,
  onError,
  ...props 
}) => {
  const [failed, setFailed] = useState(false);
  const [isVisible, setIsVisible] = useState(priority); // Priority images are immediately visible
  const [isLoaded, setIsLoaded] = useState(false);
  const [isAnimated, setIsAnimated] = useState(false);
  const elementRef = useRef(null);
  const timeoutRef = useRef(null);

  // Handle visibility changes
  const handleVisibilityChange = useCallback((visible) => {
    if (visible && !isVisible) {
      // Add staggered delay for non-priority images
      if (delay > 0) {
        timeoutRef.current = setTimeout(() => {
          setIsVisible(true);
        }, delay);
      } else {
        setIsVisible(true);
      }
    }
  }, [isVisible, delay]);

  // Set up intersection observer
  useEffect(() => {
    const element = elementRef.current;
    if (!element || priority) return;

    const observer = getIntersectionObserver();
    observedElements.set(element, handleVisibilityChange);
    observer.observe(element);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      observer.unobserve(element);
      observedElements.delete(element);
    };
  }, [handleVisibilityChange, priority]);

  // Preload priority images
  useEffect(() => {
    if (priority && src && !failed) {
      preloadImage(src).catch(() => setFailed(true));
    }
  }, [src, priority, failed]);

  const handleError = useCallback((error) => {
    setFailed(true);
    if (onError) onError(error);
  }, [onError]);

  const handleLoad = useCallback((event) => {
    setIsLoaded(true);
    // Add a small delay before animation for smoother effect
    setTimeout(() => setIsAnimated(true), 50);
    if (onLoad) onLoad(event);
  }, [onLoad]);

  if (!src || src === "" || failed) {
    return (
      <div 
        ref={elementRef}
        className={`${className} transition-opacity duration-300 ${isAnimated ? 'opacity-100' : 'opacity-80'}`}
      >
        {fallback}
      </div>
    );
  }

  if (!isVisible) {
    return (
      <div 
        ref={elementRef}
        className={`${className} image-shimmer`}
        style={{ minHeight: '200px' }}
      />
    );
  }

  return (
    <div ref={elementRef} className="relative">
      {/* Loading skeleton */}
      {!isLoaded && (
        <div className={`absolute inset-0 image-shimmer`} />
      )}
      
      <LazyLoadImage 
        src={src} 
        alt={alt}
        className={`${className} transition-all duration-500 ease-out ${
          isLoaded && isAnimated 
            ? 'opacity-100 scale-100' 
            : 'opacity-0 scale-105'
        }`}
        onError={handleError}
        onLoad={handleLoad}
        effect="blur"
        threshold={50}
        {...props} 
      />
    </div>
  );
};

export default ImageWithFallback;